import { Component, inject, input, output } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { TeamMemberStatus } from '@core/tellent-types';
import type {
  BadgeColor,
  IconName,
  RtButtonColorContextVariant,
  RtButtonVariant,
  RtInfoboxButtonVariant,
  RtWalkthroughButtonVariant,
} from '@core/user-interface-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  HeadingComponent,
  IconComponent,
} from '@core/user-interface-legacy';

export type StatusProps = {
  cta: {
    text: string;
    icon: IconName;
    variant:
      | RtButtonVariant
      | RtInfoboxButtonVariant
      | RtButtonColorContextVariant
      | RtWalkthroughButtonVariant;
  };
  badge: {
    text: string;
    color: BadgeColor;
  };
  heading: string;
  description: string;
};

export type EditMemberStatus = Record<TeamMemberStatus, StatusProps>;

@Component({
  selector: 'tlc-edit-member-status',
  imports: [HeadingComponent, ButtonComponent, BadgeComponent, IconComponent],
  templateUrl: './edit-member-status.component.html',
  styleUrl: './edit-member-status.component.less',
})
export class EditMemberStatusComponent {
  public currentStatus = input.required<TeamMemberStatus>();
  public actionClicked = output<TeamMemberStatus>();

  protected i18n = inject(I18nService);

  protected config: EditMemberStatus = {
    [TeamMemberStatus.NotInvited]: {
      cta: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.not_invited.cta'),
        icon: 'send',
        variant: 'secondary',
      },
      badge: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.not_invited.badge'),
        color: 'draft',
      },
      heading: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.common.invitation_status',
      ),
      description: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.not_invited.description',
      ),
    },
    [TeamMemberStatus.Invited]: {
      cta: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.invited.cta'),
        icon: 'replay',
        variant: 'outline',
      },
      badge: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.invited.badge'),
        color: 'requested-tint',
      },
      heading: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.common.invitation_status',
      ),
      description: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.invited.description',
      ),
    },
    [TeamMemberStatus.Active]: {
      cta: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.active.cta'),
        icon: 'send',
        variant: 'outline',
      },
      badge: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.active.badge'),
        color: 'success-tint',
      },
      heading: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.common.invitation_status',
      ),
      description: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.active.description',
      ),
    },
    [TeamMemberStatus.Deactivated]: {
      cta: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.deactivated.cta'),
        icon: 'member-activate',
        variant: 'secondary',
      },
      badge: {
        text: this.i18n.translate('tlc.members.edit_member.invitation_status.deactivated.badge'),
        color: 'warning-tint',
      },
      heading: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.common.account_status',
      ),
      description: this.i18n.translate(
        'tlc.members.edit_member.invitation_status.deactivated.description',
      ),
    },
  };
}
